import { useEffect, useState } from 'react';
import { reviewApi } from '../../api/services';
import { PRINCIPAL_CHANGED_EVENT } from '../../api/client';
import type { ReviewSummary } from '../../types/models';
import { REVIEW_SUMMARY_REFRESH_EVENTS } from './reviewEvents';

/** How often the counts are read while the page is visible. */
export const REVIEW_SUMMARY_POLL_MS = 2 * 60_000;
/** Coming back to the window re-reads, unless the last read was this recent. */
export const REVIEW_SUMMARY_FOCUS_GAP_MS = 15_000;

function same(a: ReviewSummary | null, b: ReviewSummary): boolean {
  return !!a
    && a.total === b.total
    && a.transcriptActions === b.transcriptActions
    && a.imageTasks === b.imageTasks
    && a.voiceNotes === b.voiceNotes
    && a.agentProposals === b.agentProposals;
}

/**
 * How many things wait in Review (GET /review/summary), for the nav badge.
 * Null until the server has answered (and again straight after an EA switches
 * executive, until the new executive's counts arrive). Read on mount, every
 * two minutes while the page is visible, when the window regains focus, and
 * at once on any of REVIEW_SUMMARY_REFRESH_EVENTS. Reads never overlap: an
 * event during a read asks for one more read after it. A failed read keeps
 * the last answer. `enabled: false` (a platform admin, signed out) reads
 * nothing and returns null.
 */
export function useReviewSummary({ enabled = true }: { enabled?: boolean } = {}): ReviewSummary | null {
  const [summary, setSummary] = useState<ReviewSummary | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSummary(null);
      return undefined;
    }
    let disposed = false;
    let inFlight = false;
    let again = false;
    // Bumped when the executive changes: an answer for the previous one is dropped.
    let generation = 0;
    let lastReadAt = 0;

    const read = async (): Promise<void> => {
      if (disposed) return;
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      again = false;
      const asked = generation;
      lastReadAt = Date.now();
      try {
        const next = await reviewApi.summary();
        if (!disposed && asked === generation) setSummary(prev => (same(prev, next) ? prev : next));
      } catch {
        // Keep the last answer; the next poll or event tries again.
      } finally {
        inFlight = false;
        if (!disposed && (again || asked !== generation)) void read();
      }
    };

    const onEvent = () => { void read(); };
    const onFocus = () => {
      if (Date.now() - lastReadAt >= REVIEW_SUMMARY_FOCUS_GAP_MS) void read();
    };
    const onPrincipalChanged = () => {
      generation += 1;
      setSummary(null);
      void read();
    };

    void read();
    const timer = window.setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') void read();
    }, REVIEW_SUMMARY_POLL_MS);
    REVIEW_SUMMARY_REFRESH_EVENTS.forEach(name => window.addEventListener(name, onEvent));
    window.addEventListener('focus', onFocus);
    window.addEventListener(PRINCIPAL_CHANGED_EVENT, onPrincipalChanged);

    return () => {
      disposed = true;
      window.clearInterval(timer);
      REVIEW_SUMMARY_REFRESH_EVENTS.forEach(name => window.removeEventListener(name, onEvent));
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(PRINCIPAL_CHANGED_EVENT, onPrincipalChanged);
    };
  }, [enabled]);

  return summary;
}

export default useReviewSummary;
